/**
 * Bus de comunicación multi-tab
 * Usa BroadcastChannel + localStorage como fallback
 */

import { idleConfig } from './config';
import { IdleMessage } from './types';
import { RATE_LIMITS } from './constants';

type MessageHandler = (message: IdleMessage) => void;

class IdleBus {
  private handlers: Set<MessageHandler> = new Set();
  private broadcastChannel: BroadcastChannel | null = null;
  private lastActivityMessageTime = 0;
  private isDestroyed = false;

  constructor() {
    this.setupBroadcastChannel();
    this.setupStorageListener();
  }

  private setupBroadcastChannel() {
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        this.broadcastChannel = new BroadcastChannel(idleConfig.channel);
        this.broadcastChannel.addEventListener('message', this.handleBroadcastMessage);
      }
    } catch (error) {
      console.error('Error setting up BroadcastChannel:', error);
    }
  }

  private setupStorageListener() {
    window.addEventListener('storage', this.handleStorageEvent);
  }

  private handleBroadcastMessage = (event: MessageEvent<IdleMessage>) => {
    if (this.isDestroyed) return;
    this.notifyHandlers(event.data);
  };

  private handleStorageEvent = (event: StorageEvent) => {
    if (this.isDestroyed) return;

    // Solo procesar eventos del canal de idle
    if (event.key !== idleConfig.channel || !event.newValue) {
      return;
    }

    try {
      const message: IdleMessage = JSON.parse(event.newValue);
      this.notifyHandlers(message);
    } catch (error) {
      console.error('Error parsing storage message:', error);
    }
  };

  private notifyHandlers(message: IdleMessage) {
    this.handlers.forEach(handler => {
      try {
        handler(message);
      } catch (error) {
        console.error('Error in message handler:', error);
      }
    });
  }

  private shouldRateLimit(message: IdleMessage): boolean {
    if (message.type !== 'activity') return false;

    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivityMessageTime;

    if (timeSinceLastActivity < RATE_LIMITS.MIN_MESSAGE_INTERVAL_MS) {
      return true;
    }

    this.lastActivityMessageTime = now;
    return false;
  }

  private publishToStorage(message: IdleMessage) {
    try {
      const serialized = JSON.stringify(message);
      localStorage.setItem(idleConfig.channel, serialized);

      // Limpiar inmediatamente para no llenar el storage
      setTimeout(() => {
        try {
          localStorage.removeItem(idleConfig.channel);
        } catch (error) {
          console.error('Error cleaning localStorage message:', error);
        }
      }, 100);
    } catch (error) {
      console.error('Error writing message to localStorage:', error);
    }
  }

  subscribe(handler: MessageHandler): () => void {
    this.handlers.add(handler);

    return () => {
      this.handlers.delete(handler);
    };
  }

  publish(message: IdleMessage) {
    if (this.isDestroyed) return;

    // Rate limiting para mensajes de actividad
    if (this.shouldRateLimit(message)) {
      return;
    }

    // Intentar BroadcastChannel primero (solo funciona entre pestañas)
    if (this.broadcastChannel) {
      try {
        this.broadcastChannel.postMessage(message);
      } catch (error) {
        console.error('Error sending via BroadcastChannel:', error);
      }
    }

    // Siempre enviar por localStorage como fallback (para otras pestañas)
    this.publishToStorage(message);

    // IMPORTANTE: Notificar directamente a los handlers en la misma pestaña
    // porque ni BroadcastChannel ni storage events funcionan en la misma pestaña
    this.notifyHandlers(message);
  }

  destroy() {
    this.isDestroyed = true;
    this.handlers.clear();

    if (this.broadcastChannel) {
      this.broadcastChannel.removeEventListener('message', this.handleBroadcastMessage);
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }

    window.removeEventListener('storage', this.handleStorageEvent);
  }
}

// Singleton instance
let busInstance: IdleBus | null = null;

export const getBus = (): IdleBus => {
  if (!busInstance) {
    busInstance = new IdleBus();
  }
  return busInstance;
};

export const destroyBus = () => {
  if (busInstance) {
    busInstance.destroy();
    busInstance = null;
  }
};
